import { useState } from 'react';
import { Paper, Stack, Text } from '@mantine/core';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router';
import { Button } from 'src/design-system/Button';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useClientFamilyMap } from 'src/clients/hooks/useClientFamilyMap';
import type { CalendarEvent } from 'src/meetings/models';
import { getRecordingDataTargets, type RecordingDataTarget } from 'src/meetings/utils/event-utils';
import { useCreateRecordingDataVersion } from 'src/meetings/hooks/useCreateRecordingDataVersion';

type Props = {
  event: CalendarEvent;
};

const targetKey = (target: RecordingDataTarget): string =>
  target.kind === 'client' ? target.clientId : target.familyId;

/**
 * Lists one "add data to record" action per record a meeting's recording can be processed into:
 * one per family attending (a shared review across its members) and one per remaining client, so
 * each can process the recording independently.
 */
export const RecordingDataActions = ({ event }: Props) => {
  const navigate = useNavigate();
  const familyMap = useClientFamilyMap();
  const { createClientDataVersion, createFamilyDataVersion } = useCreateRecordingDataVersion(
    event.id,
  );
  const [pendingKey, setPendingKey] = useState<string | undefined>(undefined);

  const targets = getRecordingDataTargets(event, familyMap);

  const handleReview = async (target: RecordingDataTarget) => {
    setPendingKey(targetKey(target));
    try {
      const query = (fileId: string, extra = '') =>
        `?fileId=${fileId}&fileName=${encodeURIComponent(event.title)}` +
        `&guid=${uuidv4()}&originMeetingId=${event.id}${extra}`;

      if (target.kind === 'client') {
        const fileId = await createClientDataVersion(target.clientId);
        navigate(`/client/${target.clientId}/detected-entities${query(fileId)}`);
      } else {
        const fileId = await createFamilyDataVersion(target.familyId);
        navigate(
          `/client/${target.reviewClientId}/detected-entities${query(fileId, `&familyId=${encodeURIComponent(target.familyId)}`)}`,
        );
      }
    } catch (error) {
      notifyAndLogError('Failed to prepare data for review', 'Please try again later', error, {
        eventId: event.id,
        target: targetKey(target),
      });
      setPendingKey(undefined);
    }
  };

  return (
    <Stack gap="xs" mt="lg">
      <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
        Suggested actions
      </Text>
      {targets.map(target => (
        <Paper key={targetKey(target)} variant="card" w="328px">
          <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {target.kind === 'client'
              ? `Add data to ${target.name}'s record`
              : `Add data to the ${target.name}`}
          </Text>
          <Text mt="xxxs" mb="xs" size="xs" fw={FONT_WEIGHTS.medium} c="gray.10">
            {target.kind === 'client'
              ? "Extract this client's data from the meeting to add/update their record."
              : "Extract each family member's data from the meeting to add/update their records."}
          </Text>
          <Button
            variant="text"
            onClick={() => handleReview(target)}
            loading={pendingKey === targetKey(target)}
            disabled={!!pendingKey}
          >
            Review data
          </Button>
        </Paper>
      ))}
    </Stack>
  );
};
